import { useState } from "react";
import { Snowflake, RotateCcw, Check, Sparkles } from "lucide-react";
import { generateFrozenSteps, SUGGESTIONS } from "../../lib/frozenSteps";
import { externalLinkProps, installCta } from "../../lib/funnelLinks";

export default function Frozen() {
  const [input, setInput] = useState("");
  const [result, setResult] = useState(null);

  const handleGenerate = (text) => {
    const value = (text ?? input).trim();
    if (!value) return;
    setInput(value);
    setResult(generateFrozenSteps(value));
  };

  const handleReset = () => {
    setResult(null);
    setInput("");
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleGenerate();
    }
  };

  return (
    <section
      data-testid="frozen-section"
      className="relative px-6 lg:px-10 py-24 lg:py-32 bg-[#F8FAFC] overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -top-40 left-1/2 -translate-x-1/2 w-[40rem] h-[40rem] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(245,158,11,0.10) 0%, rgba(248,250,252,0) 60%)",
        }}
      />

      <div className="relative max-w-4xl mx-auto text-center fade-up">
        <p className="font-label text-sm tracking-[0.18em] uppercase font-semibold text-[#F59E0B]">
          Try it now · no install needed
        </p>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-[#0F172A]">
          Type what you&rsquo;re stuck on. Get one tiny way in.
        </h2>
        <p className="mt-5 text-lg text-[#475569] leading-relaxed max-w-2xl mx-auto">
          This is a taste of the &ldquo;I&rsquo;m Frozen&rdquo; button. The
          real one runs in your browser side panel and reads the page
          you&rsquo;re actually on.
        </p>
      </div>

      <div className="relative max-w-2xl mx-auto mt-14">
        <div className="bg-white border border-[#E2E8F0] rounded-3xl p-7 lg:p-9 shadow-[0_20px_60px_rgba(15,23,42,0.06)]">
          <label
            htmlFor="frozen-input"
            className="font-label text-xs tracking-[0.18em] uppercase font-semibold text-[#475569]"
          >
            What are you stuck on?
          </label>
          <div className="mt-2 flex items-stretch gap-2">
            <input
              id="frozen-input"
              data-testid="frozen-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="e.g. Write client proposal"
              className="flex-1 min-w-0 font-display text-lg text-[#0F172A] placeholder:text-[#94A3B8] bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl px-4 py-3.5 focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent transition-all"
            />
          </div>

          {/* Suggestion chips */}
          <div className="mt-3 flex flex-wrap gap-2">
            {SUGGESTIONS.map((s, i) => (
              <button
                key={i}
                type="button"
                data-testid={`frozen-suggestion-${i}`}
                onClick={() => handleGenerate(s)}
                className="text-xs font-medium text-[#475569] bg-[#F8FAFC] hover:bg-[#DBEAFE] hover:text-[#1d4ed8] border border-[#E2E8F0] rounded-full px-3 py-1.5 transition-colors"
              >
                {s}
              </button>
            ))}
          </div>

          {!result ? (
            <>
              {/* chaotic preview */}
              <div
                data-testid="frozen-chaos"
                className="relative h-28 mt-6 rounded-2xl bg-[#F8FAFC] border border-dashed border-[#E2E8F0] overflow-hidden"
              >
                {[
                  { t: "where do I start?", x: "8%", y: "30%", r: "-rotate-3" },
                  { t: "too big…", x: "62%", y: "20%", r: "rotate-2" },
                  { t: "tomorrow?", x: "30%", y: "65%", r: "-rotate-1" },
                  { t: "ugh", x: "75%", y: "55%", r: "rotate-3" },
                  { t: "what was the…", x: "45%", y: "40%", r: "rotate-1" },
                ].map((b, i) => (
                  <span
                    key={i}
                    style={{ left: b.x, top: b.y }}
                    className={`absolute font-display text-sm text-[#94A3B8] ${b.r}`}
                  >
                    {b.t}
                  </span>
                ))}
              </div>

              <button
                data-testid="frozen-button"
                onClick={() => handleGenerate()}
                type="button"
                disabled={!input.trim()}
                className="mr-breathe mt-6 w-full bg-[#F9735B] hover:bg-[#e85f47] disabled:bg-[#FFE4DE] disabled:text-[#F9735B]/70 disabled:cursor-not-allowed disabled:shadow-none text-white rounded-2xl py-4 font-medium text-base inline-flex items-center justify-center gap-2 transition-colors shadow-[0_10px_28px_rgba(249,115,91,0.32)]"
              >
                <Snowflake className="w-4 h-4" strokeWidth={1.75} />
                I&rsquo;m Frozen — break this down
              </button>
              <p className="text-xs text-[#475569] mt-3 text-center">
                Type a task or pick a suggestion above.
              </p>
            </>
          ) : (
            <div className="mt-6">
              <div className="flex items-center justify-between gap-3 pb-4 border-b border-dashed border-[#E2E8F0]">
                <div className="min-w-0">
                  <p className="font-label text-[11px] tracking-[0.18em] uppercase font-semibold text-[#475569]">
                    You&rsquo;re stuck on
                  </p>
                  <p
                    data-testid="frozen-task"
                    className="font-display text-lg sm:text-xl text-[#0F172A] mt-0.5 font-semibold truncate"
                  >
                    {result.task}
                  </p>
                </div>
                <button
                  data-testid="frozen-reset"
                  onClick={handleReset}
                  className="text-xs text-[#475569] hover:text-[#0F172A] inline-flex items-center gap-1.5 shrink-0 px-3 py-2 rounded-full hover:bg-[#F8FAFC] transition-colors"
                  type="button"
                >
                  <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Try another
                </button>
              </div>

              <div className="mt-5 flex items-center gap-2">
                <Sparkles
                  className="w-3.5 h-3.5 text-[#7C3AED]"
                  strokeWidth={1.75}
                />
                <p className="font-label text-[11px] tracking-[0.18em] uppercase font-semibold text-[#7C3AED]">
                  Mindrail suggests
                </p>
              </div>

              <ol data-testid="frozen-steps" className="mt-3 space-y-2.5">
                {result.steps.map((s, i) => (
                  <li
                    key={i}
                    className="fade-up is-visible flex items-start gap-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-3.5"
                    style={{ animationDelay: `${i * 80}ms` }}
                  >
                    <span className="mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-white border border-[#E2E8F0] font-label text-[11px] font-semibold text-[#2563EB] shrink-0">
                      {i + 1}
                    </span>
                    <p className="font-display text-[16px] text-[#0F172A] leading-snug flex-1">
                      {s}
                    </p>
                    <Check
                      className="w-4 h-4 text-[#22C55E] shrink-0 mt-1"
                      strokeWidth={2}
                    />
                  </li>
                ))}
              </ol>

              <div className="mt-6 rounded-2xl bg-gradient-to-r from-[#FEF3C7] via-[#FFE4DE] to-[#EDE9FE] p-[1px]">
                <div className="rounded-2xl bg-white px-4 py-3.5 flex items-start gap-3">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-[#FEF3C7] shrink-0">
                    <Snowflake
                      className="w-3.5 h-3.5 text-[#F59E0B]"
                      strokeWidth={1.75}
                    />
                  </span>
                  <p className="text-sm text-[#475569] leading-snug">
                    The real Mindrail also reads the page you&rsquo;re on
                    so its steps fit your actual context.{" "}
                    <a
                      href={installCta.href}
                      data-testid="frozen-cta-install"
                      className="text-[#2563EB] hover:text-[#1d4ed8] font-medium"
                      {...externalLinkProps(installCta.external)}
                    >
                      Add to Chrome →
                    </a>
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
